import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Circle,
  Download,
  Bot,
  ChevronDown,
  ChevronRight,
  Phone,
  Plus,
  Clock,
  Mic,
  MicOff,
  Cloud,
  CloudOff,
  Loader2,
  Sparkles,
  Info,
  Play
} from "lucide-react";
import { api, vendorCallNotesApi } from "@/services/api";
import StartEvaluationModal from "@/components/modals/StartEvaluationModal";
import type { Research, UpdateVendorCallNotesInput, Workspace, WorkspaceVendor } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  GLEAN_VENDOR_CALL_QUESTIONS,
  GLEAN_VENDOR_CALL_CATEGORIES,
} from "@/data/glean-mock";

/**
 * Debounce utility function
 */
function debounce<T extends (...args: Parameters<T>) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface Question {
  id: string;
  category: string;
  question: string;
  answered: boolean;
  answeredByAI: boolean;
  answer: string;
  aiSuggested?: boolean;
  context?: string;
  frameworkTag?: string;
}

interface AINote {
  id: string;
  timestamp: string;
  category: string;
  content: string;
}

// Generic discovery questions (not vendor-specific)
interface DiscoveryQuestion {
  id: string;
  message: string;
  options: string[];
}

const DISCOVERY_QUESTIONS: DiscoveryQuestion[] = [
  {
    id: "budget",
    message: "Before I prepare your vendor call questions, let me understand your situation.\n\nWhat's your budget range for this type of tool?",
    options: [
      "Under $50k/year",
      "$50k\u2013200k/year",
      "$200k+",
      "Still exploring",
    ],
  },
  {
    id: "priority",
    message: "Got it. And what matters most to you in this evaluation?",
    options: [
      "Data privacy & security",
      "AI transparency",
      "Integration flexibility",
      "Total cost of ownership",
      "Ease of adoption",
    ],
  },
  {
    id: "familiarity",
    message: "Last question \u2014 how familiar is your team with this vendor already?",
    options: [
      "We've never used it",
      "We've seen a demo",
      "We're in a trial / POC",
      "We use it but evaluating alternatives",
    ],
  },
];

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function ProgressBar({ progress, answered, total }: { progress: number; answered: number; total: number }) {
  const getProgressColor = (percent: number) => {
    if (percent === 100) return 'bg-emerald-500';
    if (percent >= 75) return 'bg-green-500';
    if (percent >= 50) return 'bg-yellow-500';
    if (percent >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getProgressGlow = (percent: number) => {
    if (percent === 100) return 'shadow-emerald-500/50';
    if (percent >= 75) return 'shadow-green-500/50';
    if (percent >= 50) return 'shadow-yellow-500/50';
    if (percent >= 25) return 'shadow-orange-500/50';
    return 'shadow-red-500/50';
  };

  const progressColor = getProgressColor(progress);
  const glowColor = getProgressGlow(progress);

  return (
    <div className="flex items-center gap-3">
      <div className="w-32 h-2.5 bg-muted/30 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${progressColor} ${progress > 0 ? `shadow-md ${glowColor}` : ''}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="flex items-center gap-1.5">
        <span className={`text-sm font-bold ${progress === 100 ? 'text-emerald-600' : 'text-foreground'}`}>
          {answered}/{total}
        </span>
        {progress === 100 && (
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        )}
      </div>
    </div>
  );
}

const FRAMEWORK_CHIP_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  SEE:    { bg: 'bg-[#4DA6D4]/10', text: 'text-[#4DA6D4]', border: 'border-[#4DA6D4]/30' },
  CHANGE: { bg: 'bg-[#9B4FD4]/10', text: 'text-[#9B4FD4]', border: 'border-[#9B4FD4]/30' },
  ADAPT:  { bg: 'bg-[#5BBFB5]/10', text: 'text-[#5BBFB5]', border: 'border-[#5BBFB5]/30' },
  USE:    { bg: 'bg-[#C47B9F]/10', text: 'text-[#C47B9F]', border: 'border-[#C47B9F]/30' },
  LEARN:  { bg: 'bg-[#5C6BC0]/10', text: 'text-[#5C6BC0]', border: 'border-[#5C6BC0]/30' },
  EXIT:   { bg: 'bg-[#C4785A]/10', text: 'text-[#C4785A]', border: 'border-[#C4785A]/30' },
};

function FrameworkChip({ tag }: { tag: string }) {
  const style = FRAMEWORK_CHIP_STYLES[tag];
  if (!style) return null;

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-bold tracking-wider uppercase ${style.bg} ${style.text} ${style.border}`}>
      {tag}
    </span>
  );
}

/**
 * Discovery Dialog Component — 3 step-by-step questions
 */
function DiscoveryDialog({
  vendorName,
  onComplete
}: {
  vendorName: string;
  onComplete: (answers: { budget: string; priority: string; familiarity: string }) => void;
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const handleAnswer = (questionId: string, answer: string) => {
    const newAnswers = { ...answers, [questionId]: answer };
    setAnswers(newAnswers);

    if (currentStep < DISCOVERY_QUESTIONS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete({
        budget: newAnswers.budget,
        priority: newAnswers.priority,
        familiarity: newAnswers.familiarity,
      });
    }
  };

  const currentQuestion = DISCOVERY_QUESTIONS[currentStep];

  return (
    <div className="max-w-lg mx-auto">
      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {DISCOVERY_QUESTIONS.map((_, i) => (
          <div
            key={i}
            className={`w-2.5 h-2.5 rounded-full transition-all ${
              i < currentStep
                ? 'bg-[#EAA059]'
                : i === currentStep
                  ? 'bg-[#EAA059] scale-125'
                  : 'bg-muted-foreground/20'
            }`}
          />
        ))}
      </div>

      {/* Question */}
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#EAA059] to-[#d4914d] flex items-center justify-center flex-shrink-0 mt-0.5">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              Preparing questions for <span className="font-semibold text-foreground">{vendorName}</span>
            </p>
            <p className="text-base text-foreground whitespace-pre-line">
              {currentQuestion.message}
            </p>
          </div>
        </div>

        {/* Options */}
        <div className="space-y-2 pl-11">
          {currentQuestion.options.map((option) => (
            <button
              key={option}
              onClick={() => handleAnswer(currentQuestion.id, option)}
              className="w-full text-left px-4 py-3 rounded-lg border border-border hover:border-[#EAA059]/50 hover:bg-[#EAA059]/5 transition-all text-sm text-foreground"
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {/* Step indicator */}
      <p className="text-xs text-muted-foreground text-center mt-4">
        Question {currentStep + 1} of {DISCOVERY_QUESTIONS.length}
      </p>
    </div>
  );
}

/**
 * Questionnaire page for vendor evaluation calls.
 *
 * Both admins and users see the same workspace-based flow:
 *   1. Select a vendor (from workspace, must have a completed evaluation)
 *   2. Answer 3 discovery questions (budget, priority, familiarity)
 *   3. AI generates tailored vendor call questions
 *
 * The only role difference: admins can access ?vendor=glean-demo (hardcoded demo).
 */
export default function QuestionnairePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Glean demo mode: only accessible by admins
  const isGleanDemo = searchParams.get('vendor') === 'glean-demo' && isAdmin;

  // Pre-select vendor from URL (e.g. navigating from interactive experience)
  const preselectedVendorId = searchParams.get('vendorId');

  // Workspace-based vendor selection state
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');
  const [workspaceVendors, setWorkspaceVendors] = useState<WorkspaceVendor[]>([]);
  const [selectedWorkspaceVendor, setSelectedWorkspaceVendor] = useState<WorkspaceVendor | null>(null);

  // Discovery flow state
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [aiQuestionsLoaded, setAiQuestionsLoaded] = useState(false);

  // Glean demo: pre-built questions
  const initialQuestions: Question[] = isGleanDemo
    ? GLEAN_VENDOR_CALL_QUESTIONS.map(q => ({
        id: q.id,
        category: q.category,
        question: q.question,
        answered: false,
        answeredByAI: false,
        answer: '',
        aiSuggested: q.aiSuggested,
        context: q.context,
        frameworkTag: q.criterion_id,
      }))
    : [];

  // Legacy vendors state (only used for Glean demo)
  const [vendors, setVendors] = useState<Research[]>([]);

  const [selectedVendor, setSelectedVendor] = useState<string>(isGleanDemo ? 'https://glean.com' : '');
  const [questions, setQuestions] = useState<Question[]>(initialQuestions);
  const [isLoading, setIsLoading] = useState(!isGleanDemo);
  const [userNotes, setUserNotes] = useState<string>('');
  const [aiNotes, setAiNotes] = useState<AINote[]>([]);
  const [isAIActive, setIsAIActive] = useState(false);
  const [callStartTime, setCallStartTime] = useState<number | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(isGleanDemo ? ['AI Transparency'] : [])
  );
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [mobileTab, setMobileTab] = useState<'questions' | 'notes' | 'ai'>('questions');
  const [isAddVendorModalOpen, setIsAddVendorModalOpen] = useState(false);

  const aiNotesRef = useRef<HTMLDivElement>(null);
  const lastSavedRef = useRef<string>('');

  // Load workspaces (for all roles except Glean demo)
  useEffect(() => {
    if (isGleanDemo) return;

    const loadWorkspaces = async () => {
      try {
        const { workspaces: ws } = await api.listWorkspaces();
        setWorkspaces(ws);
        if (ws.length === 1) {
          setSelectedWorkspaceId(ws[0].id);
        }
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to load workspaces:', err);
        setIsLoading(false);
      }
    };
    loadWorkspaces();
  }, [isGleanDemo]);

  // Load workspace vendors when workspace is selected
  useEffect(() => {
    if (!selectedWorkspaceId || isGleanDemo) return;

    const loadWorkspaceVendors = async () => {
      setIsLoading(true);
      try {
        const { vendors: wv } = await api.getWorkspaceVendors(selectedWorkspaceId);
        // Only show vendors with completed evaluations
        const evaluatedVendors = wv.filter(v => v.has_evaluation && v.evaluation_status === 'completed');
        setWorkspaceVendors(evaluatedVendors);
      } catch (err) {
        console.error('Failed to load workspace vendors:', err);
        setWorkspaceVendors([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadWorkspaceVendors();
  }, [selectedWorkspaceId, isGleanDemo]);

  // Auto-select vendor from URL param (e.g. coming from interactive experience)
  useEffect(() => {
    if (!preselectedVendorId || isGleanDemo || selectedWorkspaceVendor) return;
    if (workspaceVendors.length === 0) return;

    const match = workspaceVendors.find(v => v.vendor_id === preselectedVendorId);
    if (match) {
      setSelectedWorkspaceVendor(match);
      setSelectedVendor(match.vendor_domain || match.vendor_name);
      setShowDiscovery(true);
      setAiQuestionsLoaded(false);
      setQuestions([]);
    }
  }, [preselectedVendorId, workspaceVendors, isGleanDemo, selectedWorkspaceVendor]);

  // Glean demo: inject mock vendor
  useEffect(() => {
    if (!isGleanDemo) return;
    setVendors([{
      id: "glean-demo",
      domain: "https://glean.com",
      url: "https://glean.com",
      name: "Glean",
      status: "fresh",
      pages_collected: 0,
      gemini_file_id: null,
      metadata_tags: [],
      last_scanned: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }]);
    setIsLoading(false);
  }, [isGleanDemo]);

  // Load notes when vendor changes
  useEffect(() => {
    if (!selectedVendor) return;
    if (isGleanDemo) {
      setIsLoadingNotes(false);
      return;
    }

    const loadNotes = async () => {
      setIsLoadingNotes(true);
      try {
        const { notes } = await vendorCallNotesApi.getVendorCallNotes(selectedVendor);
        if (notes) {
          setUserNotes(notes.user_notes || '');
          setAiNotes(notes.ai_notes || []);

          if (notes.generated_questions && notes.generated_questions.length > 0) {
            const restored: Question[] = notes.generated_questions.map(gq => {
              const savedStatus = notes.questions_status?.find(s => s.question_id === gq.id);
              return {
                id: gq.id,
                category: gq.category,
                question: gq.question,
                answered: savedStatus?.answered ?? false,
                answeredByAI: savedStatus?.answered_by_ai ?? false,
                answer: savedStatus?.answer ?? '',
                aiSuggested: gq.aiSuggested ?? true,
                context: gq.context,
                frameworkTag: gq.criterion_id,
              };
            });
            setQuestions(restored);
            const categories = [...new Set(notes.generated_questions.map(q => q.category))];
            setExpandedCategories(new Set(categories));
            setAiQuestionsLoaded(true);
            setShowDiscovery(false);
          } else if (notes.questions_status && notes.questions_status.length > 0) {
            setQuestions(prev => prev.map(q => {
              const savedStatus = notes.questions_status.find(s => s.question_id === q.id);
              if (savedStatus) {
                return {
                  ...q,
                  answered: savedStatus.answered,
                  answeredByAI: savedStatus.answered_by_ai || false,
                  answer: savedStatus.answer || '',
                };
              }
              return q;
            }));
          }

          lastSavedRef.current = notes.user_notes || '';
        } else {
          setUserNotes('');
          setAiNotes([]);
          setQuestions([]);
          lastSavedRef.current = '';
        }
      } catch (err) {
        console.error('Failed to load notes:', err);
      } finally {
        setIsLoadingNotes(false);
      }
    };

    loadNotes();
  }, [selectedVendor, isGleanDemo]);

  // Debounced save function
  const saveNotes = useCallback(async (updates: UpdateVendorCallNotesInput) => {
    if (!selectedVendor) return;
    if (isGleanDemo) {
      if (updates.user_notes !== undefined) {
        lastSavedRef.current = updates.user_notes;
      }
      return;
    }

    setSaveStatus('saving');
    try {
      await vendorCallNotesApi.updateVendorCallNotes(selectedVendor, updates);
      setSaveStatus('saved');
      if (updates.user_notes !== undefined) {
        lastSavedRef.current = updates.user_notes;
      }
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Failed to save notes:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [selectedVendor, isGleanDemo]);

  const debouncedSaveNotes = useMemo(
    () => debounce((updates: UpdateVendorCallNotesInput) => saveNotes(updates), 500),
    [saveNotes]
  );

  const handleUserNotesChange = (value: string) => {
    setUserNotes(value);
    if (value !== lastSavedRef.current) {
      debouncedSaveNotes({ user_notes: value });
    }
  };

  const saveQuestionsStatus = useCallback((updatedQuestions: Question[]) => {
    if (!selectedVendor) return;
    const questionsStatus = updatedQuestions.map(q => ({
      question_id: q.id,
      answered: q.answered,
      answered_by_ai: q.answeredByAI,
      answer: q.answer,
    }));
    debouncedSaveNotes({ questions_status: questionsStatus });
  }, [selectedVendor, debouncedSaveNotes]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (callStartTime) {
      interval = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [callStartTime]);

  useEffect(() => {
    if (aiNotesRef.current) {
      aiNotesRef.current.scrollTop = aiNotesRef.current.scrollHeight;
    }
  }, [aiNotes]);

  // Handle workspace vendor selection
  const handleWorkspaceVendorSelect = (vendorId: string) => {
    const vendor = workspaceVendors.find(v => v.vendor_id === vendorId);
    if (vendor) {
      setSelectedWorkspaceVendor(vendor);
      setSelectedVendor(vendor.vendor_domain || vendor.vendor_name);
      // Reset state and start discovery flow
      setShowDiscovery(true);
      setAiQuestionsLoaded(false);
      setQuestions([]);
    }
  };

  // Handle discovery completion — call AI to generate questions
  const handleDiscoveryComplete = async (answers: { budget: string; priority: string; familiarity: string }) => {
    if (!selectedWorkspaceVendor?.evaluation_id) return;

    setShowDiscovery(false);
    setIsGeneratingQuestions(true);

    try {
      const result = await vendorCallNotesApi.generateCallQuestions({
        evaluation_id: selectedWorkspaceVendor.evaluation_id,
        budget: answers.budget,
        priority: answers.priority,
        familiarity: answers.familiarity,
      });

      if (result.success && result.questions.length > 0) {
        const aiQuestions: Question[] = result.questions.map(q => ({
          id: q.id,
          category: q.category,
          question: q.question,
          answered: false,
          answeredByAI: false,
          answer: '',
          aiSuggested: true,
          context: q.context,
          frameworkTag: q.criterion_id,
        }));
        setQuestions(aiQuestions);
        const categories = [...new Set(result.questions.map(q => q.category))];
        setExpandedCategories(new Set(categories));
        setAiQuestionsLoaded(true);

        saveNotes({
          generated_questions: result.questions.map(q => ({
            id: q.id,
            category: q.category,
            question: q.question,
            context: q.context,
            criterion_id: q.criterion_id,
            aiSuggested: true,
          })),
        });
      } else {
        console.error('Failed to generate questions:', result.error);
        setQuestions([]);
        setAiQuestionsLoaded(false);
      }
    } catch (err) {
      console.error('Failed to generate questions:', err);
      setQuestions([]);
      setAiQuestionsLoaded(false);
    } finally {
      setIsGeneratingQuestions(false);
    }
  };

  const toggleQuestion = (questionId: string) => {
    setQuestions(prev => {
      const updated = prev.map(q =>
        q.id === questionId ? { ...q, answered: !q.answered } : q
      );
      saveQuestionsStatus(updated);
      return updated;
    });
  };

  const updateQuestionAnswer = (questionId: string, answer: string) => {
    setQuestions(prev => {
      const updated = prev.map(q =>
        q.id === questionId ? { ...q, answer } : q
      );
      saveQuestionsStatus(updated);
      return updated;
    });
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const startCall = () => {
    setCallStartTime(Date.now());
    setIsAIActive(true);

    setTimeout(() => {
      const answerText = 'AES-256 encryption for data at rest, TLS 1.3 for data in transit.';
      const newNote: AINote = {
        id: crypto.randomUUID(),
        timestamp: formatTime(3),
        category: 'Security',
        content: 'Vendor confirmed AES-256 encryption for data at rest and TLS 1.3 for data in transit.'
      };
      setAiNotes(prev => {
        const updated = [...prev, newNote];
        debouncedSaveNotes({ ai_notes: updated });
        return updated;
      });
      setQuestions(prev => {
        const updated = prev.map(q =>
          q.id === 'sec1' ? { ...q, answered: true, answeredByAI: true, answer: answerText } : q
        );
        saveQuestionsStatus(updated);
        return updated;
      });
    }, 3000);

    setTimeout(() => {
      const answerText = 'SOC 2 Type II (current), ISO 27001 (in progress), GDPR compliant.';
      const newNote: AINote = {
        id: crypto.randomUUID(),
        timestamp: formatTime(7),
        category: 'Compliance',
        content: 'Certifications mentioned: SOC 2 Type II (current), ISO 27001 (in progress), GDPR compliant.'
      };
      setAiNotes(prev => {
        const updated = [...prev, newNote];
        debouncedSaveNotes({ ai_notes: updated });
        return updated;
      });
      setQuestions(prev => {
        const updated = prev.map(q =>
          q.id === 'comp1' ? { ...q, answered: true, answeredByAI: true, answer: answerText } : q
        );
        saveQuestionsStatus(updated);
        return updated;
      });
    }, 7000);

    setTimeout(() => {
      const answerText = 'REST and GraphQL APIs. SDKs for Python, Node.js, and Go.';
      const newNote: AINote = {
        id: crypto.randomUUID(),
        timestamp: formatTime(12),
        category: 'Technical',
        content: 'REST and GraphQL APIs available. SDK support for Python, Node.js, and Go.'
      };
      setAiNotes(prev => {
        const updated = [...prev, newNote];
        debouncedSaveNotes({ ai_notes: updated });
        return updated;
      });
      setQuestions(prev => {
        const updated = prev.map(q =>
          q.id === 'tech1' ? { ...q, answered: true, answeredByAI: true, answer: answerText } : q
        );
        saveQuestionsStatus(updated);
        return updated;
      });
    }, 12000);
  };

  const endCall = () => {
    setCallStartTime(null);
    setIsAIActive(false);
  };

  const resetCall = () => {
    setQuestions(prev => prev.map(q => ({ ...q, answered: false, answeredByAI: false, answer: '' })));
    setUserNotes('');
    setAiNotes([]);
    setCallStartTime(null);
    setCallDuration(0);
    setIsAIActive(false);
  };

  const handleExport = () => {
    const vendorName = selectedWorkspaceVendor
      ? selectedWorkspaceVendor.vendor_name
      : vendors.find(v => v.domain === selectedVendor)?.name || selectedVendor;
    const answeredQuestions = questions.filter(q => q.answered);
    const unansweredQuestions = questions.filter(q => !q.answered);

    const content = `VENDOR CALL SUMMARY
==================
Vendor: ${vendorName}
Date: ${new Date().toLocaleDateString()}
Duration: ${formatTime(callDuration)}

PROGRESS: ${answeredQuestions.length}/${questions.length} questions answered

ANSWERED QUESTIONS
------------------
${answeredQuestions.map((q, i) =>
  `${i + 1}. [${q.category}] ${q.question}${q.answeredByAI ? ' (AI detected)' : ''}
   Answer: ${q.answer || '(No answer recorded)'}`
).join('\n\n')}

UNANSWERED QUESTIONS
--------------------
${unansweredQuestions.map((q, i) =>
  `${i + 1}. [${q.category}] ${q.question}`
).join('\n')}

AI NOTES
--------
${aiNotes.map(note => `[${note.timestamp}] ${note.category}: ${note.content}`).join('\n\n')}

YOUR NOTES
----------
${userNotes || '(No notes taken)'}
`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor-call-${vendorName}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const answeredCount = questions.filter(q => q.answered).length;
  const aiAnsweredCount = questions.filter(q => q.answeredByAI).length;
  const progressPercent = (answeredCount / questions.length) * 100;

  const questionsByCategory = questions.reduce((acc, question) => {
    if (!acc[question.category]) {
      acc[question.category] = [];
    }
    acc[question.category].push(question);
    return acc;
  }, {} as Record<string, Question[]>);

  const categoryOrder = isGleanDemo
    ? GLEAN_VENDOR_CALL_CATEGORIES
    : aiQuestionsLoaded
      ? [...new Set(questions.map(q => q.category))]
      : ['Security', 'Compliance', 'Technical', 'Commercial', 'Implementation'];

  // The main questionnaire is visible when we have a vendor selected and are past discovery
  const hasVendorSelected = isGleanDemo
    || (selectedWorkspaceVendor && !showDiscovery && !isGeneratingQuestions);

  // Onboarding state: no vendor selected yet, showing the 3-step card
  const isOnboarding = !isGleanDemo && !selectedWorkspaceVendor && !aiQuestionsLoaded && !showDiscovery && !isGeneratingQuestions;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Page Title */}
      {!isGleanDemo && (
        <div className="px-6 py-4 border-b border-border bg-card/30 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-foreground">
            Run through these questions when you've booked a call with the vendor
          </h1>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/vendor-call/demo')}
              className="gap-2 border-[#EAA059]/30 text-[#b47a3a] hover:bg-[#EAA059]/10 hover:border-[#EAA059]/50 flex-shrink-0"
            >
              <Play className="w-3.5 h-3.5" />
              Demo Mode
            </Button>
          )}
        </div>
      )}

      {/* AI-Prepared Banner (Glean demo) */}
      {isGleanDemo && (
        <div className="border-b border-[#EAA059]/20 bg-gradient-to-r from-[#EAA059]/5 via-[#EAA059]/10 to-[#EAA059]/5">
          <div className="px-6 py-2.5 flex items-center gap-3">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#EAA059] to-[#d4914d] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <p className="text-sm text-foreground">
              <span className="font-semibold text-[#b47a3a]">AI-prepared questions</span>
              <span className="text-muted-foreground"> — These questions were tailored by your FeedForward assistant based on the Glean evaluation report.</span>
            </p>
          </div>
        </div>
      )}

      {/* AI-Prepared Banner (after AI questions loaded) */}
      {!isGleanDemo && aiQuestionsLoaded && (
        <div className="border-b border-[#EAA059]/20 bg-gradient-to-r from-[#EAA059]/5 via-[#EAA059]/10 to-[#EAA059]/5">
          <div className="px-6 py-2.5 flex items-center gap-3">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#EAA059] to-[#d4914d] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <p className="text-sm text-foreground">
              <span className="font-semibold text-[#b47a3a]">AI-prepared questions</span>
              <span className="text-muted-foreground"> — Tailored based on the {selectedWorkspaceVendor?.vendor_name} evaluation report and your priorities.</span>
            </p>
          </div>
        </div>
      )}

      {/* Compact Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-3 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink min-w-0">
              {callStartTime && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-full flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-sm font-medium text-green-600 dark:text-green-400 hidden sm:inline">Live Call</span>
                </div>
              )}

              {/* Vendor Selection */}
              {isGleanDemo ? (
                // Glean demo: static dropdown
                <select
                  value={selectedVendor}
                  disabled
                  className="px-3 py-1.5 border border-border rounded-lg bg-background text-foreground text-sm font-medium min-w-0 max-w-[140px] sm:max-w-none"
                >
                  {vendors.map((vendor) => (
                    <option key={vendor.domain} value={vendor.domain}>
                      {vendor.name || vendor.domain}
                    </option>
                  ))}
                </select>
              ) : isOnboarding ? null : (
                // Workspace-based selection (all roles) — hidden during onboarding
                <div className="flex items-center gap-2">
                  {workspaces.length > 1 && (
                    <select
                      value={selectedWorkspaceId}
                      onChange={(e) => {
                        setSelectedWorkspaceId(e.target.value);
                        setSelectedWorkspaceVendor(null);
                        setShowDiscovery(false);
                        setAiQuestionsLoaded(false);
                      }}
                      className="px-3 py-1.5 border border-border rounded-lg bg-background text-foreground text-sm font-medium focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 max-w-[140px] sm:max-w-none"
                    >
                      <option value="">Select workspace</option>
                      {workspaces.map((ws) => (
                        <option key={ws.id} value={ws.id}>{ws.name}</option>
                      ))}
                    </select>
                  )}
                  {(selectedWorkspaceId || workspaces.length === 1) && !isLoading && (
                    <div className="relative">
                      <select
                        value={selectedWorkspaceVendor?.vendor_id || ''}
                        onChange={(e) => handleWorkspaceVendorSelect(e.target.value)}
                        className={`px-3 py-1.5 border rounded-lg bg-background text-foreground text-sm font-medium focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 max-w-[180px] sm:max-w-none ${
                          !selectedWorkspaceVendor
                            ? 'border-[#EAA059] ring-2 ring-[#EAA059]/30 animate-pulse'
                            : 'border-border'
                        }`}
                      >
                        <option value="">Select vendor</option>
                        {workspaceVendors.map((v) => (
                          <option key={v.vendor_id} value={v.vendor_id}>
                            {v.vendor_name}
                          </option>
                        ))}
                      </select>
                      {!selectedWorkspaceVendor && workspaceVendors.length > 0 && (
                        <span className="absolute -top-1 -right-1 flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EAA059] opacity-75" />
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-[#EAA059]" />
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {hasVendorSelected && (
                <>
                  {!callStartTime ? (
                    <div className="flex items-center gap-2.5">
                      <div className="hidden lg:flex items-center gap-2.5 px-4 py-2 rounded-lg border border-blue-400/30 bg-blue-50">
                        <span className="text-xs font-bold tracking-wide text-white bg-blue-500 rounded-full px-3 py-1 shrink-0">
                          Coming Soon
                        </span>
                        <span className="text-sm text-blue-700 leading-tight whitespace-nowrap">
                          AI assistant that listens, suggests follow-ups, and guides your call
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-muted/50 opacity-50 cursor-default">
                        <Mic className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="hidden sm:inline text-sm font-medium text-muted-foreground">Start Call</span>
                        <span className="sm:hidden text-xs font-bold tracking-wide text-white bg-blue-500 rounded-full px-2.5 py-0.5 ml-1">
                          Soon
                        </span>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={endCall}
                      className="flex items-center gap-2"
                    >
                      <MicOff className="w-4 h-4" />
                      <span className="hidden sm:inline">End Call</span>
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={resetCall} className="hidden sm:flex">
                    Reset
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExport}>
                    <Download className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          {hasVendorSelected && (
            <div className="flex items-center gap-4 sm:gap-6 mt-2">
              <div className="flex flex-col gap-1 flex-1">
                <ProgressBar progress={progressPercent} answered={answeredCount} total={questions.length} />
                {aiAnsweredCount > 0 && (
                  <div className="flex items-center gap-1 text-xs text-blue-500">
                    <Bot className="w-3 h-3" />
                    <span>{aiAnsweredCount} detected by AI</span>
                  </div>
                )}
              </div>

              {callStartTime && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/30 rounded-lg flex-shrink-0">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-mono font-medium text-foreground">{formatTime(callDuration)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Tab Navigation */}
      {hasVendorSelected && (
        <div className="md:hidden border-b border-border bg-card/30">
          <div className="flex">
            <button
              onClick={() => setMobileTab('questions')}
              className={`flex-1 px-4 py-2.5 text-sm font-semibold text-center transition-colors ${
                mobileTab === 'questions'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Questions
            </button>
            <button
              onClick={() => setMobileTab('notes')}
              className={`flex-1 px-4 py-2.5 text-sm font-semibold text-center transition-colors ${
                mobileTab === 'notes'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Your Notes
            </button>
            <button
              onClick={() => setMobileTab('ai')}
              className={`flex-1 px-4 py-2.5 text-sm font-semibold text-center transition-colors relative ${
                mobileTab === 'ai'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              AI Notes
              {isAIActive && (
                <span className="absolute top-2 right-4 w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Discovery Dialog */}
      {!isGleanDemo && selectedWorkspaceVendor && showDiscovery && (
        <div className="flex-1 flex items-center justify-center p-6">
          <DiscoveryDialog
            vendorName={selectedWorkspaceVendor.vendor_name}
            onComplete={handleDiscoveryComplete}
          />
        </div>
      )}

      {/* Loading state while generating questions */}
      {!isGleanDemo && isGeneratingQuestions && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#EAA059]/20 to-[#d4914d]/20 flex items-center justify-center mx-auto">
              <Loader2 className="w-8 h-8 text-[#EAA059] animate-spin" />
            </div>
            <div>
              <p className="text-foreground font-medium">Generating tailored questions...</p>
              <p className="text-sm text-muted-foreground mt-1">
                Analyzing the {selectedWorkspaceVendor?.vendor_name} evaluation report
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content — Two Column Layout: Questions + Notes */}
      {hasVendorSelected ? (
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden relative">
          {/* Questions Column — 55% */}
          <div className={`${mobileTab === 'questions' ? 'flex-1' : 'hidden'} md:block md:w-[55%] border-r overflow-y-auto ${
            isGleanDemo || aiQuestionsLoaded
              ? 'border-[#EAA059]/15 bg-[#fdf8f3]'
              : 'border-border'
          }`}>
            <div className="p-4 pb-16">
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-sm font-semibold uppercase tracking-wide ${
                  isGleanDemo || aiQuestionsLoaded ? 'text-[#8b5e34]' : 'text-foreground'
                }`}>Questions</h2>
                {(isGleanDemo || aiQuestionsLoaded) && (
                  <span className="text-xs text-[#b47a3a] font-medium">{questions.length} questions</span>
                )}
              </div>

              <div className="space-y-2.5">
                {categoryOrder.map(category => {
                  const categoryQuestions = questionsByCategory[category] || [];
                  const categoryAnswered = categoryQuestions.filter(q => q.answered).length;
                  const isExpanded = expandedCategories.has(category);
                  const isAiStyled = isGleanDemo || aiQuestionsLoaded;

                  return (
                    <div key={category} className={`rounded-xl overflow-hidden shadow-sm ${
                      isAiStyled
                        ? 'border border-[#EAA059]/20'
                        : 'border border-border'
                    }`}>
                      <button
                        onClick={() => toggleCategory(category)}
                        className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                          isAiStyled
                            ? 'bg-gradient-to-r from-[#EAA059] to-[#d4914d] hover:from-[#d4914d] hover:to-[#c48a50]'
                            : 'bg-muted/30 hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className={`w-4 h-4 ${isAiStyled ? 'text-white/80' : 'text-muted-foreground'}`} />
                          ) : (
                            <ChevronRight className={`w-4 h-4 ${isAiStyled ? 'text-white/80' : 'text-muted-foreground'}`} />
                          )}
                          <span className={`font-semibold ${isAiStyled ? 'text-white' : 'text-foreground'}`}>{category}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${
                            categoryAnswered === categoryQuestions.length
                              ? isAiStyled ? 'text-white' : 'text-green-600'
                              : isAiStyled ? 'text-white/70' : 'text-muted-foreground'
                          }`}>
                            {categoryAnswered}/{categoryQuestions.length}
                          </span>
                          {categoryAnswered === categoryQuestions.length && (
                            <CheckCircle2 className={`w-4 h-4 ${isAiStyled ? 'text-white' : 'text-green-600'}`} />
                          )}
                        </div>
                      </button>

                      {isExpanded && (
                        <div className={`divide-y ${isAiStyled ? 'divide-[#EAA059]/10' : 'divide-border'}`}>
                          {categoryQuestions.map((question) => (
                            <div
                              key={question.id}
                              className={`transition-all ${
                                isAiStyled
                                  ? question.answered
                                    ? question.answeredByAI
                                      ? 'bg-blue-50 border-l-2 border-l-blue-400'
                                      : 'bg-emerald-50 border-l-2 border-l-emerald-400'
                                    : 'bg-white'
                                  : question.answered
                                    ? question.answeredByAI
                                      ? 'bg-blue-500/10 border-l-2 border-l-blue-500'
                                      : 'bg-emerald-500/10 border-l-2 border-l-emerald-500'
                                    : 'bg-background'
                              }`}
                            >
                              <button
                                onClick={() => toggleQuestion(question.id)}
                                className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-all ${
                                  isAiStyled
                                    ? question.answered
                                      ? 'hover:bg-black/[0.02]'
                                      : 'hover:bg-[#EAA059]/[0.04]'
                                    : question.answered
                                      ? 'hover:bg-black/5 dark:hover:bg-white/5'
                                      : 'hover:bg-muted/30'
                                }`}
                              >
                                <div className="flex-shrink-0 mt-0.5">
                                  {question.answered ? (
                                    <CheckCircle2 className={`w-5 h-5 ${
                                      question.answeredByAI ? 'text-blue-500' : 'text-emerald-500'
                                    }`} />
                                  ) : (
                                    <Circle className={`w-5 h-5 ${isAiStyled ? 'text-[#EAA059]/40' : 'text-muted-foreground/50'}`} />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <p className={`text-sm leading-relaxed ${
                                      isAiStyled
                                        ? question.answered ? 'text-slate-500' : 'text-slate-800'
                                        : question.answered ? 'text-foreground/80' : 'text-foreground'
                                    }`}>
                                      {question.question}
                                    </p>
                                    {question.frameworkTag && (
                                      <FrameworkChip tag={question.frameworkTag} />
                                    )}
                                  </div>
                                  {question.context && (
                                    <div className="flex items-start gap-1.5 mt-1.5">
                                      <Info className="w-3 h-3 text-[#d4914d] mt-0.5 flex-shrink-0" />
                                      <span className={`text-xs leading-relaxed ${isAiStyled ? 'text-[#a07040]' : 'text-muted-foreground'}`}>{question.context}</span>
                                    </div>
                                  )}
                                  {question.aiSuggested && !question.answeredByAI && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <Sparkles className="w-3 h-3 text-[#EAA059]" />
                                      <span className="text-xs text-[#b47a3a] font-medium">AI suggested</span>
                                    </div>
                                  )}
                                  {question.answeredByAI && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <Bot className="w-3 h-3 text-blue-500" />
                                      <span className="text-xs text-blue-500 font-medium">AI detected</span>
                                    </div>
                                  )}
                                </div>
                              </button>

                              {question.answered && (
                                <div className="px-4 pb-3 pl-12">
                                  <textarea
                                    value={question.answer}
                                    onChange={(e) => updateQuestionAnswer(question.id, e.target.value)}
                                    placeholder={question.answeredByAI ? "AI captured this answer. Add your notes..." : "Add the answer or key points..."}
                                    className={`w-full text-sm p-2 rounded-lg border resize-none focus:outline-none focus:ring-1 transition-all ${
                                      isAiStyled
                                        ? 'bg-white border-[#EAA059]/20 text-slate-700 placeholder:text-slate-400 focus:ring-[#EAA059]/30 focus:border-[#EAA059]/40'
                                        : question.answeredByAI
                                          ? 'bg-background/50 border-blue-200 dark:border-blue-800 focus:ring-blue-500/30 focus:border-blue-400'
                                          : 'bg-background/50 border-emerald-200 dark:border-emerald-800 focus:ring-emerald-500/30 focus:border-emerald-400'
                                    }`}
                                    rows={2}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* User Notes Column — 45% */}
          <div className={`${mobileTab === 'notes' ? 'flex-1' : 'hidden'} md:flex md:w-[45%] border-r border-border flex-col`}>
            <div className="px-4 py-3 border-b border-border bg-muted/20">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Your Notes</h2>
                <div className="flex items-center gap-1.5">
                  {saveStatus === 'saving' && (
                    <>
                      <Loader2 className="w-3 h-3 text-muted-foreground animate-spin" />
                      <span className="text-xs text-muted-foreground">Saving...</span>
                    </>
                  )}
                  {saveStatus === 'saved' && (
                    <>
                      <Cloud className="w-3 h-3 text-green-500" />
                      <span className="text-xs text-green-500">Saved</span>
                    </>
                  )}
                  {saveStatus === 'error' && (
                    <>
                      <CloudOff className="w-3 h-3 text-red-500" />
                      <span className="text-xs text-red-500">Error</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 p-4 pb-16">
              <textarea
                value={userNotes}
                onChange={(e) => handleUserNotesChange(e.target.value)}
                placeholder="Quick notes during the call...

- Key points
- Follow-up items
- Questions to clarify"
                className="w-full h-full resize-none border border-border rounded-lg p-3 bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                disabled={isLoadingNotes}
              />
            </div>
          </div>

          {/* AI Notes — mobile tab only (coming soon) */}
          <div className={`${mobileTab === 'ai' ? 'flex-1' : 'hidden'} md:hidden flex-col`}>
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center space-y-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#EAA059]/20 to-[#d4914d]/20 flex items-center justify-center mx-auto">
                  <Bot className="w-7 h-7 text-[#EAA059]" />
                </div>
                <p className="text-sm font-medium text-foreground">AI Assistant</p>
                <p className="text-xs text-muted-foreground max-w-[220px]">Listens in the background, suggests follow-up questions, and guides you through the call — coming soon</p>
              </div>
            </div>
          </div>

        </div>
      ) : (
        // Empty state / loading (only when not in discovery or generating)
        !showDiscovery && !isGeneratingQuestions && (
          <div className="flex-1 flex items-center justify-center p-6">
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-muted-foreground">Loading vendors...</p>
              </div>
            ) : workspaceVendors.length === 0 && selectedWorkspaceId ? (
              <div className="text-center space-y-3">
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto">
                  <Phone className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">No vendors with completed evaluations in this workspace.</p>
                <p className="text-sm text-muted-foreground">Add a vendor and run an evaluation first, then come back to prepare for the call.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddVendorModalOpen(true)}
                  className="mt-2"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add Vendor
                </Button>
              </div>
            ) : (
              /* Guided onboarding: how the page works */
              <div className="max-w-md w-full">
                <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                  <h2 className="text-base font-semibold text-foreground text-center mb-6">
                    Prepare for your vendor call in 3 steps
                  </h2>

                  <div className="space-y-5">
                    {/* Step 1 */}
                    <div className="flex items-start gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${
                        workspaceVendors.length > 0
                          ? 'bg-[#EAA059] text-white'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        1
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">Select your vendor</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {workspaceVendors.length > 0
                            ? 'Pick a vendor from the dropdown below to get started.'
                            : 'No evaluated vendors yet. Run an evaluation from the dashboard first.'
                          }
                        </p>
                      </div>
                    </div>

                    {/* Vendor selector inline below step 1 */}
                    {workspaceVendors.length > 0 && (
                      <div className="flex items-center gap-2 pl-12">
                        {workspaces.length > 1 && (
                          <select
                            value={selectedWorkspaceId}
                            onChange={(e) => {
                              setSelectedWorkspaceId(e.target.value);
                              setSelectedWorkspaceVendor(null);
                              setShowDiscovery(false);
                              setAiQuestionsLoaded(false);
                            }}
                            className="px-3 py-1.5 border border-border rounded-lg bg-background text-foreground text-sm font-medium focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                          >
                            <option value="">Select workspace</option>
                            {workspaces.map((ws) => (
                              <option key={ws.id} value={ws.id}>{ws.name}</option>
                            ))}
                          </select>
                        )}
                        <div className="relative">
                          <select
                            value={selectedWorkspaceVendor?.vendor_id || ''}
                            onChange={(e) => handleWorkspaceVendorSelect(e.target.value)}
                            className="px-3 py-1.5 border rounded-lg bg-background text-foreground text-sm font-medium focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 border-[#EAA059] ring-2 ring-[#EAA059]/30 animate-pulse"
                          >
                            <option value="">Select vendor</option>
                            {workspaceVendors.map((v) => (
                              <option key={v.vendor_id} value={v.vendor_id}>
                                {v.vendor_name}
                              </option>
                            ))}
                          </select>
                          <span className="absolute -top-1 -right-1 flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EAA059] opacity-75" />
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#EAA059]" />
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Step 2 */}
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-sm font-bold text-muted-foreground">
                        2
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">Answer 3 quick questions</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Your budget, priorities, and familiarity help us tailor the questions.
                        </p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-sm font-bold text-muted-foreground">
                        3
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">Get AI-generated call questions</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Based on the evaluation report and your answers, we generate targeted questions for your call.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      )}

      {/* Add Vendor Modal */}
      <StartEvaluationModal
        isOpen={isAddVendorModalOpen}
        onClose={() => {
          setIsAddVendorModalOpen(false);
          // Reload workspace vendors to pick up newly added/evaluated vendors
          if (selectedWorkspaceId) {
            const reload = async () => {
              try {
                const { vendors: wv } = await api.getWorkspaceVendors(selectedWorkspaceId);
                const evaluatedVendors = wv.filter(v => v.has_evaluation && v.evaluation_status === 'completed');
                setWorkspaceVendors(evaluatedVendors);
              } catch (err) {
                console.error('Failed to reload workspace vendors:', err);
              }
            };
            reload();
          }
        }}
        onStartEvaluation={async (vendorId) => {
          if (selectedWorkspaceId && vendorId && vendorId !== 'pending') {
            try {
              await api.addVendorToWorkspace(selectedWorkspaceId, vendorId, 'public');
            } catch {
              // Vendor may already be in workspace
            }
          }
        }}
        workspaceId={selectedWorkspaceId || undefined}
      />
    </div>
  );
}
